import React, { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { PenTool, Clock, BookAudio, Star } from "lucide-react";
import CatalogBrowser from "../components/CatalogBrowser";
import { useCatalog } from "../context/CatalogContext";
import { formatDuration } from "../mock";

/** The catalogue scoped to one author, with the search controls kept. */
export default function AuthorPage() {
  const { name } = useParams();
  const author = decodeURIComponent(name || "");
  const { books } = useCatalog();

  const stats = useMemo(() => {
    const mine = books.filter((b) => b.author === author);
    const hours = Math.round(mine.reduce((s, b) => s + (b.duration || 0), 0) / 3600);
    const rated = mine.filter((b) => b.ratingCount > 0);
    const avg = rated.length ? rated.reduce((s, b) => s + b.rating, 0) / rated.length : 0;
    const narrators = [...new Set(mine.map((b) => b.narrator).filter(Boolean))];
    return { count: mine.length, hours, avg, narrators };
  }, [books, author]);

  const header = (
    <div className="mb-8">
      <div className="flex items-center gap-5">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center text-brand-950 text-3xl font-bold shrink-0">
          {author.charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-widest text-brand-400 font-semibold flex items-center gap-1.5">
            <PenTool className="w-3.5 h-3.5" /> Author
          </p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1">{author}</h1>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-2 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <BookAudio className="w-4 h-4" />
              {stats.count} {stats.count === 1 ? "audiobook" : "audiobooks"}
            </span>
            {stats.hours > 0 && (
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" /> {formatDuration(stats.hours * 3600)} of listening
              </span>
            )}
            {stats.avg > 0 && (
              <span className="flex items-center gap-1.5 text-brand-400 font-medium">
                <Star className="w-4 h-4" fill="currentColor" /> {stats.avg.toFixed(1)} average
              </span>
            )}
          </div>
        </div>
      </div>

      {stats.narrators.length > 0 && (
        <p className="mt-5 text-sm text-muted-foreground">
          Narrated by{" "}
          {stats.narrators.map((n, i) => (
            <React.Fragment key={n}>
              {i > 0 && (i === stats.narrators.length - 1 ? " and " : ", ")}
              <Link
                to={`/narrator/${encodeURIComponent(n)}`}
                className="text-foreground hover:text-brand-400 transition-colors"
              >
                {n}
              </Link>
            </React.Fragment>
          ))}
          .
        </p>
      )}
    </div>
  );

  return <CatalogBrowser scope={{ author }} header={header} />;
}
